/**
 * API Client for interacting with the ASP.NET Core backend.
 * Automatically attaches the JWT authorization token and handles 401 errors.
 */

import { SERVER_ISSUE_MESSAGE, type NetworkRequestInit } from "./network-fetch";

// Define the base URL for the API
const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:5026/api";

interface ApiOptions extends NetworkRequestInit {
  data?: any;
}

export class ApiError extends Error {
  public status: number;
  public data: any;

  constructor(status: number, message: string, data?: any) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

// ── Token-change subscription ─────────────────────────────────────────────────
// The proactive-refresh scheduler needs to re-arm every time the token changes,
// and token writes happen from login, refresh, impersonate/stopImpersonation —
// none of which are visible to React state. This tiny pub/sub bridges the gap
// without coupling client.ts to the scheduler (scheduler subscribes; client
// publishes).

type AccessTokenListener = (token: string | null) => void;

const tokenListeners = new Set<AccessTokenListener>();

/**
 * Registers a listener that fires after every token write or removal.
 * Returns an unsubscribe function.
 */
export function onAccessTokenChange(listener: AccessTokenListener): () => void {
  tokenListeners.add(listener);
  return () => {
    tokenListeners.delete(listener);
  };
}

/**
 * Notifies all registered listeners of a token change.
 *
 * Each listener is called inside its own try/catch so a throwing subscriber
 * cannot prevent the others from running or corrupt the caller's control flow
 * (the caller is usually setAccessToken inside refreshAccessToken — breaking it
 * would lose the new token).
 */
function notifyTokenListeners(token: string | null): void {
  tokenListeners.forEach((listener) => {
    try {
      listener(token);
    } catch {
      // Intentionally swallowed. A subscriber bug must not break token storage.
    }
  });
}

/**
 * Retrieves the JWT access token from localStorage.
 */
export function getAccessToken(): string | null {
  return localStorage.getItem("access_token");
}

/**
 * Saves the JWT access token to localStorage.
 */
export function setAccessToken(token: string): void {
  localStorage.setItem("access_token", token);
  localStorage.setItem("accessToken", token);
  notifyTokenListeners(token);
}

/**
 * Removes the JWT access token from localStorage.
 */
export function removeAccessToken(): void {
  localStorage.removeItem("access_token");
  localStorage.removeItem("accessToken");
  notifyTokenListeners(null);
}

/**
 * True when the current access token is an impersonation token.
 *
 * Read from the token's own claims rather than a separate flag, so it cannot disagree with the token
 * actually being sent. Matters here because an impersonation session must NOT take the silent-refresh
 * path below: the refresh cookie in the browser belongs to the SuperAdmin, so refreshing would swap a
 * "viewing as student" session for a real admin one behind the user's back. The backend refuses this
 * too (409); this check just avoids making a request we know will fail.
 */
export function isImpersonating(): boolean {
  const token = getAccessToken();
  if (!token) return false;
  try {
    const base64Url = token.split(".")[1];
    if (!base64Url) return false;
    const payload = JSON.parse(atob(base64Url.replace(/-/g, "+").replace(/_/g, "/")));
    return Boolean(payload?.ImpersonatedBy);
  } catch {
    return false;
  }
}

/**
 * Endpoints that must never carry a Bearer token — they are [AllowAnonymous] on the backend.
 * Sending a stale token on these produces false "JWT authentication failed" warnings in SystemLogs.
 */
const ANONYMOUS_AUTH_ENDPOINTS = [
  "/Auth/login",
  "/Auth/register",
  "/Auth/forgot-password",
  "/Auth/reset-password",
  "/Auth/refresh",
];

export function isAnonymousAuthEndpoint(url: string): boolean {
  return ANONYMOUS_AUTH_ENDPOINTS.some((ep) => url.includes(ep));
}

/**
 * Single-flight refresh.
 *
 * Every in-flight request can 401 at the same moment the access token expires, and each would
 * otherwise fire its own /refresh. Since refresh tokens are single-use and rotated, the first call
 * would succeed and the rest would present the now-revoked value — which the backend correctly reads
 * as token replay and responds to by revoking the whole chain, logging the user out for real. Sharing
 * one promise is what keeps a burst of concurrent 401s from looking like an attack.
 *
 * Cross-tab coordination: the refresh cookie is per-browser, shared by every tab. Two tabs with an
 * expired token would each call /refresh with the same cookie value. `navigator.locks` ensures only
 * one tab refreshes at a time; the epoch check inside the lock detects that another tab already
 * refreshed and skips the network call. Falls back to the in-context single-flight when
 * `navigator.locks` is unavailable (non-secure context, older Safari) — the backend's 30s grace
 * window covers that residual case.
 */
let refreshInFlight: Promise<boolean> | null = null;

export async function refreshAccessToken(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      // Capture the token before acquiring the lock. If another tab (or an
      // earlier call in this tab) refreshed while we waited, the stored token
      // will have changed — skip the network call entirely.
      const tokenBefore = getAccessToken();

      const doRefresh = async (): Promise<boolean> => {
        // Epoch check: if the token changed while we waited for the lock,
        // another context already refreshed. Use the new token.
        if (getAccessToken() !== tokenBefore) return true;

        // VITE_API_URL already ends in /api, so the path here is relative to that — not "/api/Auth/...".
        const response = await fetch(`${API_BASE_URL}/Auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          // The refresh token is an httpOnly cookie; without this it is not sent cross-origin.
          credentials: "include",
          // The expired access token identifies which session is being renewed. The refresh token
          // itself is deliberately never in the body — the cookie is the only place it lives.
          body: JSON.stringify({ accessToken: getAccessToken() }),
        });

        if (!response.ok) return false;

        const data = await response.json();
        if (!data?.token) return false;

        setAccessToken(data.token);
        return true;
      };

      // Cross-tab lock: only one tab refreshes at a time. Falls back to the
      // existing in-context single-flight when navigator.locks is unavailable
      // (non-secure context, older Safari). The backend grace window covers
      // the residual race in that fallback.
      if (typeof navigator !== "undefined" && navigator.locks) {
        return await navigator.locks.request("lms-token-refresh", doRefresh);
      }

      return await doRefresh();
    } catch {
      // Network failure is not an auth failure, but there is no new token either way.
      return false;
    } finally {
      // Cleared in `finally` so a failed refresh does not poison every later attempt with a
      // permanently-resolved-false promise.
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/**
 * Core fetch wrapper
 */
async function fetchApi<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { data, headers: customHeaders, ...customConfig } = options;

  // Handle absolute URLs (if endpoint starts with http:// or https://)
  const url = endpoint.startsWith("http")
    ? endpoint
    : `${API_BASE_URL}${endpoint.startsWith("/") ? endpoint : `/${endpoint}`}`;

  // Rebuilt per attempt so the retry below picks up the token the refresh just stored, rather than
  // replaying the expired one it already failed with.
  const send = () => {
    const token = getAccessToken();
    const skipAuth = isAnonymousAuthEndpoint(url);
    const headers: HeadersInit = {
      "Content-Type": "application/json",
      ...(token && !skipAuth ? { Authorization: `Bearer ${token}` } : {}),
      ...customHeaders,
    };

    const config: RequestInit = {
      method: data ? "POST" : "GET",
      ...customConfig,
      headers,
      // Lets the refresh cookie ride along on API calls. Required for the /refresh call itself; the
      // cookie is Path-scoped to /api/Auth so ordinary endpoints never actually receive it.
      credentials: "include",
    };

    if (data) {
      config.body = JSON.stringify(data);
    }

    return fetch(url, config);
  };

  let response = await send();

  // An expired access token is the ordinary case now that they last ~20 minutes, so a 401 means "try
  // renewing" before it means "sign out". Only after the refresh itself fails does this become a real
  // logout — which is what stops a user being ejected mid-task every twenty minutes.
  if (response.status === 401 && !isImpersonating()) {
    if (await refreshAccessToken()) {
      response = await send();
    }
  }

  if (!response.ok) {
    // A 5xx is the server failing, not the caller doing anything wrong, and the body is typically a
    // stack trace, an empty ProblemDetails, or an HTML error page from a proxy. Passing that through to
    // the `toast.error(err.message)` convention used at ~50 call sites shows the user something they can
    // neither act on nor understand; the status is the honest signal. Distinct from the "no internet"
    // case, which never reaches here — a request that never arrived rejects out of `send()` above.
    if (response.status >= 500) {
      throw new ApiError(response.status, SERVER_ISSUE_MESSAGE, null);
    }

    if (response.status === 401) {
      // Refresh was skipped, or ran and failed: the session is genuinely over.
      removeAccessToken();
      window.dispatchEvent(new Event("auth:unauthorized"));
    }

    // 409 from the refresh endpoint means an impersonation session lapsed. Surfaced as its own event
    // so the banner can say so and restore the admin, instead of the generic "signed out" path
    // dumping a SuperAdmin at the login screen for a session that was never really theirs to lose.
    if (response.status === 409 && isImpersonating()) {
      window.dispatchEvent(new Event("auth:impersonation-expired"));
    }

    const text = await response.text();
    let errorMessage = "";
    let errorData = null;

    try {
      errorData = text ? JSON.parse(text) : null;
      if (typeof errorData === "string") {
        errorMessage = errorData;
      } else {
        errorMessage = errorData?.message || errorData?.title || "API request failed";
      }
    } catch (e) {
      // If parsing JSON fails, the response is likely a plain text error message
      errorMessage = text && !text.trim().startsWith("<") ? text : "API request failed";
    }

    throw new ApiError(response.status, errorMessage, errorData || { message: text });
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return {} as T;
  }

  const text = await response.text();
  return text ? JSON.parse(text) : ({} as T);
}

/**
 * Generic API methods
 */
export const apiClient = {
  get: <T>(endpoint: string, options?: ApiOptions) =>
    fetchApi<T>(endpoint, { ...options, method: "GET" }),

  post: <T>(endpoint: string, data?: any, options?: ApiOptions) =>
    fetchApi<T>(endpoint, { ...options, method: "POST", data }),

  put: <T>(endpoint: string, data?: any, options?: ApiOptions) =>
    fetchApi<T>(endpoint, { ...options, method: "PUT", data }),

  patch: <T>(endpoint: string, data?: any, options?: ApiOptions) =>
    fetchApi<T>(endpoint, { ...options, method: "PATCH", data }),

  delete: <T>(endpoint: string, options?: ApiOptions) =>
    fetchApi<T>(endpoint, { ...options, method: "DELETE" }),
};
